// Export the rendered frame, rather than keeping the WebGL drawing buffer alive.
// html2canvas is loaded locally only when an image is requested. Its input is a
// frozen DOM copy with all canvases copied during the same MapLibre render event.
import { setStationSpriteExportDpr } from '../layers/station-sprites.js';
import { rainImageCaption } from '../lib/rain-image-caption.js';
import { state as stationState, isFutureHidden, isStationsFieldSuppressed } from '../layers/stations.js';
import { refreshStationSpriteResolution } from '../layers/stations.js';
let rendererPromise;
let imageQueue = Promise.resolve();

export function initMapImageTranslations() {
    Object.assign(window.WS_I18N_EN ||= {}, {
        'Copia PNG': 'Copy PNG', 'Copiato': 'Copied',
        'PNG copiato. Ora puoi incollarlo.': 'PNG copied. You can paste it now.',
        'Copia non riuscita. Riprova oppure scarica il PNG.': 'Copy failed. Try again or download the PNG.',
        'Copia, salva o condividi l’immagine.': 'Copy, save or share the image.',
        'Copia o scarica l’immagine.': 'Copy or download the image.',
        'Questo browser non supporta la copia delle immagini.': 'This browser does not support copying images.',
        'Immagine della mappa': 'Map image', 'Immagine': 'Image',
        'Immagine della zona visibile della mappa meteo': 'Image of the visible weather map area',
        'Preparazione dell’immagine…': 'Preparing the image…', 'Riprova': 'Try again', '↓ Scarica': '↓ Download',
        'Salva l’immagine o condividila con le tue app.': 'Save the image or share it with your apps.',
        'Scarica l’immagine per condividerla. Questo browser non supporta la condivisione diretta delle immagini.': 'Download the image to share it. This browser does not support sharing image files directly.',
        'Condivisione non riuscita. Puoi scaricare l’immagine e condividerla dalla galleria.': 'Sharing failed. You can download the image and share it from your gallery.',
        'La mappa sta ancora caricando. Attendi qualche secondo e riprova.': 'The map is still loading. Wait a few seconds and try again.',
        'Impossibile caricare il generatore di immagini. Riprova.': 'Unable to load the image generator. Try again.',
        'Impossibile generare l’immagine. Riprova.': 'Unable to generate the image. Try again.',
        'Impossibile preparare l’immagine. Riprova.': 'Unable to prepare the image. Try again.',
    });
}
const t = text => window.WSI18N?.t(text) || text;

function loadRenderer() {
    if (window.html2canvas) return Promise.resolve(window.html2canvas);
    if (!rendererPromise) rendererPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = '/assets/vendor/html2canvas/html2canvas.min.js?v=1.4.1';
        script.onload = () => resolve(window.html2canvas);
        script.onerror = () => { script.remove(); rendererPromise = null; reject(new Error('Impossibile caricare il generatore di immagini. Riprova.')); };
        document.head.appendChild(script);
    });
    return rendererPromise;
}

export function exportScale(width, height, pixelRatio = 1) {
    // Produce a sharp 4K image independently of the display's pixel density.
    // Keep very large/square exports within a 16-megapixel memory budget.
    return Math.min(4, 4096 / Math.max(width, height), Math.sqrt(16000000 / (width * height)));
}

export function imageFilename(date = new Date()) {
    const stamp = new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Rome', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' }).format(date).replace(/[^0-9]/g, '');
    return `weather-sicily-mappa-${stamp}.png`;
}

function visible(node) {
    return node && !node.closest('[hidden]') && getComputedStyle(node).display !== 'none' && node.getBoundingClientRect().width > 0;
}

function frozenMap(map) {
    return new Promise((resolve, reject) => {
        const timeout = setTimeout(() => { map.off('render', capture); reject(new Error('La mappa sta ancora caricando. Attendi qualche secondo e riprova.')); }, 10000);
        function capture() {
            if (map.isMoving() || !map.areTilesLoaded()) return;
            map.off('render', capture);
            clearTimeout(timeout);
            try {
                const original = map.getContainer();
                const rect = original.getBoundingClientRect();
                const copy = original.cloneNode(true);
                copy.removeAttribute('id');
                copy.style.cssText = `position:relative;inset:auto;width:${rect.width}px;height:${rect.height}px;overflow:hidden;`;
                const canvases = [...original.querySelectorAll('canvas')];
                copy.querySelectorAll('canvas').forEach((canvas, i) => {
                    const img = document.createElement('img');
                    img.src = canvases[i].toDataURL('image/png');
                    img.className = canvas.className;
                    img.style.cssText = canvas.style.cssText;
                    img.style.width = `${canvases[i].clientWidth}px`;
                    img.style.height = `${canvases[i].clientHeight}px`;
                    canvas.replaceWith(img);
                });
                copy.querySelectorAll('.maplibregl-control-container, .maplibregl-popup').forEach(node => node.remove());
                // Remove duplicate IDs and live animation in the frozen copy.
                copy.querySelectorAll('[id]').forEach(node => node.removeAttribute('id'));
                copy.querySelectorAll('*').forEach(node => { node.style.animation = 'none'; node.style.transition = 'none'; });
                const legends = [...document.querySelectorAll('.wsv2-legend, .wsv2-nowcast-legend')].filter(visible).map(node => node.cloneNode(true));
                const times = [...document.querySelectorAll('.wsv2-tl-stack .wsv2-nt-label')].filter(visible).map(node => node.textContent.trim());
                const select = document.getElementById('selectParametro');
                const parameter = document.getElementById('chkMarkers')?.checked !== false ? select?.selectedOptions[0]?.textContent.trim() : '';
                // Context legends describe the field actually drawn (including
                // wind mode and forecast fields that suppress station markers).
                const fieldLegend = legends.find(node => node.classList.contains('wsv2-legend'));
                const fieldTitles = [...(fieldLegend?.querySelectorAll('.wsv2-nl__title, .wsv2-legend-title, strong') || [])].map(node => (node.closest('.wsv2-legend--daily-rain') ? node.firstChild.textContent : node.textContent).trim()).map(title => /^(Radar pioggia|Rain radar)\b/.test(title) ? (window.WSI18N?.lang === 'en' ? 'Rain radar' : 'Radar pioggia') : title).filter(Boolean);
                const labels = [...document.querySelectorAll('.wsv2-toggle input:checked')]
                    .filter(input => /^(ovl-|chkRadar$|chkSatellite$|chkLightning$|chkTemporali$|chkAircraft$|chkShips$)/.test(input.id))
                    .map(input => input.id === 'chkRadar' ? (window.WSI18N?.lang === 'en' ? 'Rain radar' : 'Radar pioggia') : input.closest('label')?.querySelector('.txt')?.textContent?.trim() || input.closest('label')?.textContent?.trim()).filter(Boolean);
                const rainCaption = !stationState.options.wind && stationState.options.visible && !isFutureHidden() && !isStationsFieldSuppressed()
                    ? rainImageCaption(stationState.parametro, stationState._recentRain.meta, window.WSI18N?.lang === 'en') : null;
                const attribution = original.querySelector('.maplibregl-ctrl-attrib-inner')?.textContent.trim() || '© OpenStreetMap · Tiles © Esri';
                resolve({ copy, rainCaption, width: rect.width, height: rect.height, legends, times, title: [...new Set([...(fieldTitles.length ? fieldTitles : [parameter]), ...labels].filter(Boolean))].join(' · ') || (window.WSI18N?.lang === 'en' ? 'Weather map' : 'Mappa meteo'), attribution, date: new Date() });
            } catch (error) { reject(error); }
        }
        map.on('render', capture);
        map.triggerRepaint();
    });
}

function textNode(tag, text, css) {
    const node = document.createElement(tag);
    node.textContent = text;
    node.style.cssText = css;
    return node;
}

function makeStage(snapshot) {
    const stage = document.createElement('div');
    stage.className = 'wsv2-image-stage';
    stage.setAttribute('aria-hidden', 'true');
    stage.setAttribute('data-i18n-skip', '');
    stage.style.cssText = `position:fixed;left:0;top:0;z-index:-100;width:${snapshot.width}px;background:#fff;color:#10233c;font-family:Inter,Arial,sans-serif;`;
    stage.appendChild(snapshot.copy);
    const band = document.createElement('div');
    band.className = 'wsv2-image-glass-band';
    const mobile = snapshot.width < 650 || (window.matchMedia('(pointer: coarse)').matches && snapshot.width < 1024);
    const inset = mobile ? 10 : 12;
    if (snapshot.rainCaption) {
        const caption = document.createElement('div');
        caption.className = 'wsv2-image-rain-caption';
        caption.style.cssText = `position:absolute;left:12px;top:12px;max-width:calc(100% - 24px);box-sizing:border-box;padding:7px 10px;background:rgba(255,255,255,.94);border:1px solid rgba(72,112,143,.2);border-radius:8px;color:#10233c;`;
        caption.appendChild(textNode('div', snapshot.rainCaption.title, `font-weight:800;font-size:${mobile ? 15 : 18}px;line-height:1.2;`));
        caption.appendChild(textNode('div', snapshot.rainCaption.interval, `font-size:${mobile ? 11 : 12}px;line-height:1.35;margin-top:3px;`));
        stage.appendChild(caption);
    }

    band.style.cssText = mobile
        ? `position:absolute;left:${inset}px;right:${inset}px;bottom:${inset}px;`
        : `position:absolute;left:50%;transform:translateX(-50%);bottom:${inset}px;width:max-content;max-width:calc(100% - ${2 * inset}px);`;
    const footer = document.createElement('div');
    footer.style.cssText = `position:relative;display:grid;grid-template-columns:${mobile ? '1fr' : 'minmax(0,1fr) auto'};align-items:center;gap:${mobile ? 4 : 12}px;padding:5px 10px;border:1px solid rgba(255,255,255,.82);border-radius:13px;background:rgba(242,250,255,.68);box-shadow:0 7px 24px rgba(21,62,91,.12);--ws-fg:#10233c;--ws-fg-soft:#425b71;--ws-line:rgba(72,112,143,.18);--ws-bg-solid:rgba(255,255,255,.5);`;
    const head = document.createElement('div');
    head.style.cssText = `display:flex;align-items:center;gap:8px;`;
    const brand = document.createElement('div');
    brand.style.cssText = `flex-shrink:0;align-self:stretch;display:flex;align-items:center;padding-right:8px;border-right:1px solid rgba(72,112,143,.18);`;
    const logo = document.createElement('img');
    logo.src = '/logo.png';
    logo.style.cssText = `width:${mobile ? 44 : 60}px;height:auto;display:block;`;
    logo.alt = 'Weather Sicily';
    brand.appendChild(logo);
    head.appendChild(brand);
    const info = document.createElement('div');
    info.style.cssText = `flex:1;min-width:0;${mobile ? '' : 'max-width:420px;'}`;
    info.appendChild(textNode('div', snapshot.title, 'font-weight:800;font-size:14px;line-height:1.2;overflow-wrap:break-word;'));
    if (snapshot.times.length) info.appendChild(textNode('div', snapshot.times.join(' · '), 'font-size:11px;line-height:1.2;color:#51637d;'));
    const english = window.WSI18N?.lang === 'en';
    const stamp = new Intl.DateTimeFormat(english ? 'en-GB' : 'it-IT', { timeZone: 'Europe/Rome', dateStyle: 'short', timeStyle: 'short' }).format(snapshot.date);
    const meta = document.createElement('div');
    if (!mobile) meta.style.cssText = 'display:flex;align-items:baseline;flex-wrap:wrap;gap:0 8px;';
    meta.appendChild(textNode('div', english ? `Captured ${stamp} · Italy time` : `Vista acquisita il ${stamp} · ora italiana`, 'font-size:10px;line-height:1.2;color:#51637d;'));
    meta.appendChild(textNode('div', 'maps.weathersicily.it', 'font-size:10px;line-height:1.2;color:#0079b8;font-weight:700;'));
    info.appendChild(meta);
    head.appendChild(info);
    footer.appendChild(head);
    if (snapshot.legends.length) {
        const legends = document.createElement('div');
        legends.style.cssText = `display:flex;flex-wrap:wrap;align-items:center;gap:6px;max-width:${mobile ? snapshot.width - 56 : 460}px;`;
        snapshot.legends.forEach(node => {
            if(node.classList.contains('wsv2-legend--side')){
                const width=Math.max(180,Math.min(290,snapshot.width*.23));
                node.style.cssText=`position:absolute;right:16px;bottom:${snapshot.width<500?150:120}px;width:${width}px;max-width:calc(100% - 32px);padding:18px;background:#fffffff5;color:#10233c;border:1px solid #d8e3ea;border-radius:16px;box-shadow:0 4px 20px #10233c20;`;
                node.querySelectorAll('.wsv2-nl__title').forEach(n=>{n.style.fontSize='20px';n.style.lineHeight='1.3';});
                node.querySelectorAll('.wsv2-field-scale__ticks').forEach(n=>n.style.fontSize='21px');
                node.querySelectorAll('.wsv2-field-scale__body').forEach(n=>n.style.height=Math.min(320,snapshot.height*.43)+'px');
                stage.appendChild(node);return;
            }
            node.style.cssText = 'position:static;inset:auto;transform:none;display:block;width:240px;max-width:100%;margin:0;padding:3px 8px;background:rgba(255,255,255,.5);color:#10233c;border:1px solid rgba(72,112,143,.17);border-radius:9px;box-shadow:none;backdrop-filter:none;';
            if (node.classList.contains('wsv2-legend--daily-rain')) node.style.width = Math.min(440, snapshot.width - 56) + 'px';
            node.querySelectorAll('.wsv2-nl__item').forEach(item => { item.style.width = '224px'; item.style.maxWidth = '100%'; });
            node.querySelectorAll('.wsv2-legend-bar, .wsv2-nl__bar').forEach(bar => { bar.style.height = '6px'; });
            legends.appendChild(node);
        });
        footer.appendChild(legends);
    }
    band.appendChild(footer);
    stage.appendChild(band);
    document.body.appendChild(stage);
    return stage;
}

function drawFrostBackdrop(stage, scale) {
    const image = stage.querySelector('img.maplibregl-canvas');
    const band = stage.querySelector('.wsv2-image-glass-band');
    if (!image || !band) return;
    const { width, height, left, top } = band.getBoundingClientRect();
    const mapBounds = image.getBoundingClientRect();
    // Bake the frost into pixels: DOM exporters do not preserve backdrop-filter.
    // A small separable Gaussian also works on Safari without Canvas.filter.
    const small = document.createElement('canvas');
    small.width = Math.max(1, Math.ceil(width / 10));
    small.height = Math.max(1, Math.ceil(height / 10));
    const ctx = small.getContext('2d', { willReadFrequently: true });
    const rx = image.naturalWidth / mapBounds.width;
    const ry = image.naturalHeight / mapBounds.height;
    ctx.drawImage(image, (left - mapBounds.left) * rx, (top - mapBounds.top) * ry,
        width * rx, height * ry, 0, 0, small.width, small.height);
    const pixels = ctx.getImageData(0, 0, small.width, small.height);
    let src = pixels.data;
    const weights = [1, 4, 6, 4, 1];
    for (let pass = 0; pass < 6; pass++) {
        const dest = new Uint8ClampedArray(src.length);
        const horizontal = pass % 2 === 0;
        for (let y = 0; y < small.height; y++) for (let x = 0; x < small.width; x++) {
            const index = (y * small.width + x) * 4;
            for (let channel = 0; channel < 4; channel++) {
                let sum = 0;
                for (let k = -2; k <= 2; k++) {
                    const sx = horizontal ? Math.max(0, Math.min(small.width - 1, x + k)) : x;
                    const sy = horizontal ? y : Math.max(0, Math.min(small.height - 1, y + k));
                    sum += src[(sy * small.width + sx) * 4 + channel] * weights[k + 2];
                }
                dest[index + channel] = sum / 16;
            }
        }
        src = dest;
    }
    pixels.data.set(src);
    ctx.putImageData(pixels, 0, 0);
    const backdrop = document.createElement('canvas');
    backdrop.width = Math.ceil(width * scale);
    backdrop.height = Math.ceil(height * scale);
    backdrop.style.cssText = `position:absolute;inset:0;width:${width}px;height:${height}px;pointer-events:none;`;
    const out = backdrop.getContext('2d');
    out.imageSmoothingEnabled = true;
    out.imageSmoothingQuality = 'high';
    // Keep the baked blur inside the rounded card, leaving the surrounding
    // map intact instead of painting a full-width coloured strip.
    const radius = Math.min(13 * scale, backdrop.width / 2, backdrop.height / 2);
    out.beginPath();
    out.moveTo(radius, 0);
    out.arcTo(backdrop.width, 0, backdrop.width, backdrop.height, radius);
    out.arcTo(backdrop.width, backdrop.height, 0, backdrop.height, radius);
    out.arcTo(0, backdrop.height, 0, 0, radius);
    out.arcTo(0, 0, backdrop.width, 0, radius);
    out.closePath();
    out.clip();
    out.drawImage(small, 0, 0, backdrop.width, backdrop.height);
    band.prepend(backdrop);
}

function createImage(map) {
    // Closing and reopening the dialog must not run overlapping temporary
    // pixel-ratio changes on the same map.
    const job = imageQueue.then(() => renderImage(map));
    imageQueue = job.catch(() => {});
    return job;
}

async function renderImage(map) {
    const render = await loadRenderer();
    const previousRatio = map.getPixelRatio();
    const bounds = map.getContainer().getBoundingClientRect();
    const ratio = exportScale(bounds.width, bounds.height);
    let snapshot;
    try {
        map.setPixelRatio(ratio);
        setStationSpriteExportDpr(ratio);
        await refreshStationSpriteResolution();
        snapshot = await frozenMap(map);
    } finally {
        setStationSpriteExportDpr();
        map.setPixelRatio(previousRatio);
        void refreshStationSpriteResolution().catch(() => {});
    }
    const stage = makeStage(snapshot);
    try {
        await Promise.all([...stage.querySelectorAll('img')].map(img => img.decode()));
        const height = Math.ceil(stage.getBoundingClientRect().height);
        const scale = exportScale(snapshot.width, height, window.devicePixelRatio);
        drawFrostBackdrop(stage, scale);
        const canvas = await render(stage, { scale, height, backgroundColor: '#fff', logging: false, useCORS: true, imageTimeout: 10000,
            ignoreElements: node => node.parentElement === document.body && node !== stage });
        const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
        if (!blob) throw new Error('Impossibile generare l’immagine. Riprova.');
        return { blob, filename: imageFilename(snapshot.date), width: canvas.width, height: canvas.height };
    } finally { stage.remove(); }
}

export function openMapImage(map, trigger) {
    if (document.querySelector('.wsv2-map-image')) return;
    const dialog = document.createElement('dialog');
    dialog.className = 'wsv2-map-image';
    dialog.setAttribute('aria-labelledby', 'map-image-title');
    dialog.innerHTML = `<header><h2 id="map-image-title">Immagine della mappa</h2><button type="button" class="wsv2-iconbtn" data-close aria-label="Chiudi">✕</button></header>
        <div class="wsv2-map-image__preview"><p role="status">Preparazione dell’immagine…</p><img alt="Immagine della zona visibile della mappa meteo" hidden></div>
        <footer><p class="wsv2-map-image__hint" role="status"></p><div><button type="button" data-retry hidden>Riprova</button><button type="button" data-copy disabled>Copia PNG</button><button type="button" data-share disabled>Condividi</button><button type="button" data-download disabled>↓ Scarica</button></div></footer>`;
    document.body.appendChild(dialog);
    let url = null, file = null, busy = false;
    const preview = dialog.querySelector('img');
    const status = dialog.querySelector('.wsv2-map-image__preview p');
    const hint = dialog.querySelector('.wsv2-map-image__hint');
    const download = dialog.querySelector('[data-download]');
    const share = dialog.querySelector('[data-share]');
    const copy = dialog.querySelector('[data-copy]');
    const canCopy = !!(window.isSecureContext && navigator.clipboard?.write && typeof ClipboardItem !== 'undefined' && (!ClipboardItem.supports || ClipboardItem.supports('image/png')));
    copy.title = canCopy ? '' : t('Questo browser non supporta la copia delle immagini.');
    const retry = dialog.querySelector('[data-retry]');
    dialog.addEventListener('close', () => { if (url) URL.revokeObjectURL(url); dialog.remove(); trigger?.focus(); }, { once: true });
    dialog.querySelector('[data-close]').addEventListener('click', () => dialog.close());
    dialog.addEventListener('click', e => {
        const rect = dialog.getBoundingClientRect();
        if (e.target === dialog && (e.clientX < rect.left || e.clientX > rect.right || e.clientY < rect.top || e.clientY > rect.bottom)) dialog.close();
    });
    download.addEventListener('click', () => {
        if (!url || !file) return;
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = file.name;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
    });
    copy.addEventListener('click', async () => {
        const image = file;
        if (!image || !canCopy) return;
        copy.disabled = true;
        try {
            await navigator.clipboard.write([new ClipboardItem({ 'image/png': image })]);
            if (dialog.open && file === image) {
                copy.textContent = t('Copiato');
                hint.textContent = t('PNG copiato. Ora puoi incollarlo.');
            }
        } catch (error) {
            if (dialog.open && file === image) {
                copy.textContent = t('Copia PNG');
                hint.textContent = t('Copia non riuscita. Riprova oppure scarica il PNG.');
            }
        } finally { if (dialog.open && file === image) copy.disabled = false; }
    });
    share.addEventListener('click', async () => {
        if (!file) return;
        share.disabled = true;
        try { await navigator.share({ files: [file] }); }
        catch (error) { if (error.name !== 'AbortError') hint.textContent = t('Condivisione non riuscita. Puoi scaricare l’immagine e condividerla dalla galleria.'); }
        finally { share.disabled = false; }
    });
    async function prepare() {
        if (busy) return;
        busy = true;
        retry.hidden = true;
        status.hidden = false;
        status.textContent = t('Preparazione dell’immagine…');
        try {
            const result = await createImage(map);
            if (!dialog.open) return;
            file = new File([result.blob], result.filename, { type: 'image/png' });
            url = URL.createObjectURL(result.blob);
            preview.src = url;
            preview.hidden = false;
            status.hidden = true;
            download.disabled = false;
            copy.disabled = !canCopy;
            const canShare = !!navigator.share && !!navigator.canShare?.({ files: [file] });
            share.disabled = !canShare;
            hint.textContent = `PNG · ${result.width} × ${result.height} px. ` + t(canCopy ? (canShare ? 'Copia, salva o condividi l’immagine.' : 'Copia o scarica l’immagine.') : (canShare ? 'Salva l’immagine o condividila con le tue app.' : 'Scarica l’immagine per condividerla. Questo browser non supporta la condivisione diretta delle immagini.'));
        } catch (error) {
            if (!dialog.open) return;
            status.textContent = t(error.message || 'Impossibile preparare l’immagine. Riprova.');
            retry.hidden = false;
        } finally { busy = false; }
    }
    retry.addEventListener('click', prepare);
    dialog.showModal();
    void prepare();
}
